/** @class Categories
 *
 * @desc
 * Class that displays all the `Categories` in the `Everplan` being viewed.
 */
import PropTypes from 'prop-types'

import {List, Map} from 'immutable'
import {useEffect, useMemo, useRef, useState} from 'react'

import CategoryDetails from './CategoryDetails'
import Form from '../../../../../shared_components/forms/v2/Form'
import Fields from '../../../../../shared_components/forms/v2/fields/index'
import PrearrangedFuneralCard from '../../../../../shared_components/PrearrangedFuneralCard'

import {humanizedFullName} from '../../../../../lib/nameTools'
import {categoryItems} from '../../../../../lib/plan_data/category'
import {isFuneralHome} from '../../../lib/firmTools'
import funeralHomeDefaultViewItemIds from './funeralHomeDefaultViewItemIds'

import './categories.scss'

const inDefaultView = itemResponse => (
  funeralHomeDefaultViewItemIds.includes(itemResponse.get('item-id'))
)

const Categories = ({firm, itemResponses, ...otherProps}) => {
  const {categories, currentUser} = otherProps

  const isFuneralHomeUser = useMemo(() =>
    isFuneralHome(firm) && currentUser.get('quarterback') === false
  , [currentUser, firm])

  const defaultCategoryOption = () => {
    if (currentUser.get('quarterback'))
      return 'Life & Legacy'
    else if (isFuneralHomeUser)
      return 'Default view'
    else
      return 'All Categories'
  }

  const funeralHomeDefaultViewCategories = useMemo(() => {
    if (!isFuneralHomeUser) return categories

    return categories.filter(
      category => categoryItems(category, itemResponses).some(inDefaultView)
    )
  }, [categories, isFuneralHomeUser, itemResponses])

  const getCategoryByName = name => List([
    categories.find(category => category.get('display-name') === name)
  ])

  const defaultCategories = () => {
    const option = defaultCategoryOption()

    if (option === 'Default view') return funeralHomeDefaultViewCategories

    if (option === 'All Categories') return categories

    return getCategoryByName(option)
  }

  const formRef = useRef(null)

  const [selectedCategories, setSelectedCategories] = useState(
    defaultCategories()
  )

  const [selectedCategoryOption, setSelectedCategoryOption] = useState(
    defaultCategoryOption()
  )

  const categoryOptions = useMemo(() => {
    let options = categories
      .sortBy(category => category.get('sort-order'))
      .map(category => Map({
        label: category.get('display-name'),
        value: category.get('display-name'),
        id: category.get('id')
      }))
      .unshift(Map({
        label: 'All Categories',
        value: 'All Categories',
        id: 'all'
      }))

    if (isFuneralHomeUser) {
      options = options.unshift(
        Map({label: 'Default view', value: 'Default view', id: 'default'})
      )
    }

    return options
  }, [categories, isFuneralHomeUser])

  const filteredItemResponses = useMemo(() => {
    if (selectedCategoryOption === 'Default view')
      return itemResponses.filter(inDefaultView)

    return itemResponses
  }, [itemResponses, selectedCategoryOption])

  const handleSelectChange = () => {
    const value = formRef.current.formData().getIn(['categories', 'value'])

    setSelectedCategoryOption(value)

    if (value === 'All Categories')
      setSelectedCategories(categories)
    else if (value === 'Default view')
      setSelectedCategories(funeralHomeDefaultViewCategories)
    else
      setSelectedCategories(getCategoryByName(value))
  }

  useEffect(() => {
    if (isFuneralHomeUser && itemResponses.size > 0) {
      setSelectedCategories(defaultCategories())
      setSelectedCategoryOption(defaultCategoryOption())
    }
  }, [isFuneralHomeUser, itemResponses])

  const everplanId = otherProps.everplanSummary.get('id')
  const showPrearrangedFuneralCard = selectedCategories.find(
    category => category.get('display-name') === 'Life & Legacy'
  )

  return (
    <div className='categories forms-playground'>
      <header className='categories-header'>
        <h3>Everplan Details</h3>
        <div className='header-controls'>
          <div className='category-selection-container flex-container'>
            <span className='category-selection-prompt'>Show: </span>
            <Form ref={formRef} className='core category-selection'>
              <Fields.Select
                data={
                  Map({
                    id: 'categories',
                    items: categoryOptions,
                    legend: '',
                    name: 'categories',
                    value: selectedCategoryOption
                  })
                }
                onChange={handleSelectChange}
              />
            </Form>
          </div>
        </div>
      </header>
      {
        showPrearrangedFuneralCard &&
        <PrearrangedFuneralCard
          clientName={humanizedFullName(otherProps.clientSeat)}
          showConfirmationMessage={false}
          everplanId={everplanId}
          firmId={firm.get('id')}
        />
      }
      {
        selectedCategories.sortBy(category => category.get('sort-order')).map(
          category => (
            <CategoryDetails
              {...otherProps}
              isFuneralHome={isFuneralHome(firm)}
              itemResponses={filteredItemResponses}
              category={category}
              key={category.get('id')}
            />
          )
        )
      }
    </div>
  )
}

Categories.propTypes = {
  categories: PropTypes.instanceOf(List),
  context: PropTypes.string,
  clientSeat: PropTypes.instanceOf(Map),
  currentUser: PropTypes.instanceOf(Map),
  clientSeats: PropTypes.instanceOf(List),
  actor: PropTypes.string,
  everplanSummary: PropTypes.instanceOf(Map),
  firm: PropTypes.instanceOf(Map),
  firmOwnership: PropTypes.instanceOf(Map),
  itemResponses: PropTypes.instanceOf(List),
  firmConfig: PropTypes.instanceOf(Map),
  owners: PropTypes.instanceOf(List),
  createResource: PropTypes.func,
  everplanBundleDownload: PropTypes.instanceOf(List),
  params: PropTypes.shape({
    everplanId: PropTypes.string
  }),
  userConfig: PropTypes.instanceOf(Map)
}

export default Categories
